import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSave, FiMail, FiPhone, FiClock, FiMapPin, FiFacebook, FiInstagram, FiYoutube, FiSettings } from 'react-icons/fi';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e9ecef;
`;

const HeaderIcon = styled.div`
  width: 50px;
  height: 50px;
  background: #01675f;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
`;

const HeaderTitle = styled.h1`
  color: #01675f;
  margin: 0;
  font-size: 2rem;
`;

const HeaderSubtitle = styled.p`
  color: #6c757d;
  margin: 0;
  font-size: 1rem;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const SettingsCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
`;

const CardIcon = styled.div`
  width: 40px;
  height: 40px;
  background: #01675f;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
`;

const CardTitle = styled.h3`
  color: #01675f;
  margin: 0;
  font-size: 1.3rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #01675f;
    box-shadow: 0 0 0 3px rgba(1, 103, 95, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #01675f;
    box-shadow: 0 0 0 3px rgba(1, 103, 95, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${props => props.variant === 'primary' && `
    background: #01675f;
    color: white;
    
    &:hover {
      background: #015a52;
      transform: translateY(-2px);
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: #6c757d;
    color: white;
    
    &:hover {
      background: #5a6268;
      transform: translateY(-2px);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: #01675f;
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid #c3e6cb;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid #f5c6cb;
`;

const AdminSettingsPage = () => {
  const [settings, setSettings] = useState({
    contactInfo: {
      email: '',
      phone: '',
      businessHours: ''
    },
    socialMedia: {
      facebook: '',
      instagram: '',
      youtube: ''
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/site-settings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      } else {
        // If no settings exist, use defaults
        setSettings({
          contactInfo: {
            email: 'admin@rplsupport.com.au',
            phone: '+61 433 098 399',
            phoneSecondary: '+61 415 820 320',
            businessHours: 'Mon - Sat: 8:00 am - 7:00 pm',
            address: '200 Gilchrist Dr, Campbelltown NSW 2560'
          },
          socialMedia: {
            facebook: 'https://www.facebook.com/profile.php?id=61570517976682',
            instagram: 'https://l.instagram.com/?u=https%3A%2F%2Fwww.facebook.com%2Frplsupport.com.au%3Fmibextid%3DLQQJ4d&e=AT3GqV8_JTXeqmox0o6-gM3P4nkwJfIKyidiTA3vShUNeVS-MZY9MLHj9s1sim2frnzj3yjGit1f5-ysB0eH5QKPmn7xnpWIDmjNT3s',
            youtube: 'https://www.youtube.com/@RPLSupport_Aus'
          }
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      
      const response = await fetch('/api/admin/site-settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
        toast.success('Settings saved successfully!');
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.message || 'Failed to save settings' });
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      contactInfo: {
        email: 'admin@rplsupport.com.au',
        phone: '+61 433 098 399',
        phoneSecondary: '+61 415 820 320',
        businessHours: 'Mon - Sat: 8:00 am - 7:00 pm',
        address: '200 Gilchrist Dr, Campbelltown NSW 2560'
      },
      socialMedia: {
        facebook: 'https://www.facebook.com/profile.php?id=61570517976682',
        instagram: 'https://l.instagram.com/?u=https%3A%2F%2Fwww.facebook.com%2Frplsupport.com.au%3Fmibextid%3DLQQJ4d&e=AT3GqV8_JTXeqmox0o6-gM3P4nkwJfIKyidiTA3vShUNeVS-MZY9MLHj9s1sim2frnzj3yjGit1f5-ysB0eH5QKPmn7xnpWIDmjNT3s',
        youtube: 'https://www.youtube.com/@RPLSupport_Aus'
      }
    });
    setMessage(null);
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>Loading settings...</LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderIcon>
          <FiSettings />
        </HeaderIcon>
        <div>
          <HeaderTitle>Site Settings</HeaderTitle>
          <HeaderSubtitle>Manage contact information and social media links</HeaderSubtitle>
        </div>
      </Header>

      {message && (
        <div style={{ marginBottom: '1rem' }}>
          {message.type === 'success' ? (
            <SuccessMessage>{message.text}</SuccessMessage>
          ) : (
            <ErrorMessage>{message.text}</ErrorMessage>
          )}
        </div>
      )}

      <SettingsGrid>
        <SettingsCard>
          <CardHeader>
            <CardIcon>
              <FiMail />
            </CardIcon>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          
          <FormGroup>
            <Label><FiMail /> Email Address</Label>
            <Input
              type="email"
              value={settings.contactInfo.email}
              onChange={(e) => handleInputChange('contactInfo', 'email', e.target.value)}
              placeholder="Enter email address"
            />
          </FormGroup>
          
          <FormGroup>
            <Label><FiPhone /> Phone Number</Label>
            <Input
              type="tel"
              value={settings.contactInfo.phone}
              onChange={(e) => handleInputChange('contactInfo', 'phone', e.target.value)}
              placeholder="Enter primary phone number"
            />
          </FormGroup>
          
          <FormGroup>
            <Label><FiPhone /> Secondary Phone</Label>
            <Input
              type="tel"
              value={settings.contactInfo.phoneSecondary || ''}
              onChange={(e) => handleInputChange('contactInfo', 'phoneSecondary', e.target.value)}
              placeholder="Enter secondary phone number"
            />
          </FormGroup>
          
          <FormGroup>
            <Label><FiClock /> Business Hours</Label>
            <Input
              type="text"
              value={settings.contactInfo.businessHours}
              onChange={(e) => handleInputChange('contactInfo', 'businessHours', e.target.value)}
              placeholder="Enter business hours"
            />
          </FormGroup>
          
          <FormGroup>
            <Label><FiMapPin /> Address</Label>
            <Input
              type="text"
              value={settings.contactInfo.address || ''}
              onChange={(e) => handleInputChange('contactInfo', 'address', e.target.value)}
              placeholder="Enter business address"
            />
          </FormGroup>
        </SettingsCard>

        <SettingsCard>
          <CardHeader>
            <CardIcon>
              <FiFacebook />
            </CardIcon>
            <CardTitle>Social Media Links</CardTitle>
          </CardHeader>
          
          <FormGroup>
            <Label><FiFacebook /> Facebook URL</Label>
            <Input
              type="url"
              value={settings.socialMedia.facebook}
              onChange={(e) => handleInputChange('socialMedia', 'facebook', e.target.value)}
              placeholder="Enter Facebook URL"
            />
          </FormGroup>
          
          <FormGroup>
            <Label><FiInstagram /> Instagram URL</Label>
            <Input
              type="url"
              value={settings.socialMedia.instagram}
              onChange={(e) => handleInputChange('socialMedia', 'instagram', e.target.value)}
              placeholder="Enter Instagram URL"
            />
          </FormGroup>
          
          <FormGroup>
            <Label><FiYoutube /> YouTube URL</Label>
            <Input
              type="url"
              value={settings.socialMedia.youtube}
              onChange={(e) => handleInputChange('socialMedia', 'youtube', e.target.value)}
              placeholder="Enter YouTube URL"
            />
          </FormGroup>
          

        </SettingsCard>
      </SettingsGrid>

      <ButtonGroup>
        <Button 
          variant="primary" 
          onClick={handleSave}
          disabled={saving}
        >
          <FiSave />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
        
        <Button 
          variant="secondary" 
          onClick={handleReset}
          disabled={saving}
        >
          Reset to Defaults
        </Button>
      </ButtonGroup>
    </Container>
  );
};

export default AdminSettingsPage;